/**
 * Posts List Page
 * View and manage all posts
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBrand } from '../contexts/BrandContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/ui/ToastProvider';
import { apiClient } from '../lib/api';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Select } from '../components/ui/Select';
import { BulkScheduleModal } from '../components/BulkScheduleModal';
import { Icon } from '../components/icons';
import type { Post } from '../types';
import { format } from 'date-fns';

export function Posts() {
  const { selectedBrand } = useBrand();
  const { user } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [posts, setPosts] = useState<Post[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [selectedPosts, setSelectedPosts] = useState<string[]>([]);
  const [showBulkSchedule, setShowBulkSchedule] = useState(false);
  const [retryingId, setRetryingId] = useState<string | null>(null);

  useEffect(() => {
    if (selectedBrand) {
      fetchPosts();
    }
  }, [selectedBrand, statusFilter]);

  const fetchPosts = async () => {
    if (!selectedBrand) return;

    setIsLoading(true);
    try {
      const response = await apiClient.getPosts({
        brand_id: selectedBrand.id,
        status: statusFilter === 'all' ? undefined : statusFilter,
        limit: 50,
      });

      if (response.success && response.data) {
        setPosts(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (postId: string) => {
    try {
      await apiClient.deletePost(postId);
      setPosts(posts.filter((p) => p.id !== postId));
      setDeleteConfirm(null);
      showToast('Post deleted', 'success');
    } catch (error) {
      console.error('Failed to delete post:', error);
      showToast('Failed to delete post', 'error');
    }
  };

  const toggleSelectPost = (postId: string) => {
    setSelectedPosts(prev =>
      prev.includes(postId)
        ? prev.filter(id => id !== postId)
        : [...prev, postId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedPosts.length === posts.length) {
      setSelectedPosts([]);
    } else {
      setSelectedPosts(posts.map(p => p.id));
    }
  };

  const handleBulkScheduleComplete = () => {
    setShowBulkSchedule(false);
    setSelectedPosts([]);
    fetchPosts();
  };

  const handleRetry = async (postId: string) => {
    setRetryingId(postId);
    try {
      await apiClient.retryPost(postId);
      await fetchPosts();
      showToast('Retry queued', 'success');
    } catch (error) {
      console.error('Failed to retry post:', error);
      showToast('Failed to retry post', 'error');
    } finally {
      setRetryingId(null);
    }
  };

  if (!selectedBrand) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="text-center">
          <p className="text-gray-500">Please select a brand from the dashboard</p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => navigate('/dashboard')}
          >
            Go to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Posts</h1>
          <p className="text-gray-600 mt-1 text-sm sm:text-base">
            Manage posts for{' '}
            <span
              className="font-medium"
              style={{ color: selectedBrand.color_primary }}
            >
              {selectedBrand.name}
            </span>
          </p>
        </div>
        <Button
          onClick={() => navigate('/posts/new')}
          className="w-full sm:w-auto"
          disabled={user?.role === 'viewer'}
          variant={user?.role === 'viewer' ? 'outline' : 'primary'}
        >
          <Icon name="edit" size="sm" className="mr-2" />
          {user?.role === 'viewer' ? 'View Only' : 'Create Post'}
        </Button>
      </div>

      {/* Bulk Actions Toolbar */}
      {selectedPosts.length > 0 && (
        <Card className="bg-blue-50 border-blue-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-blue-900">
                {selectedPosts.length} post{selectedPosts.length > 1 ? 's' : ''} selected
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setSelectedPosts([])}
              >
                Clear Selection
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={() => setShowBulkSchedule(true)}
              >
                <Icon name="calendar" size="sm" className="mr-2" />
                Bulk Schedule
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <div className="flex items-center justify-between">
          <div className="max-w-xs flex-1">
            <Select
              label="Filter by Status"
              options={[
                { value: 'all', label: 'All Posts' },
                { value: 'draft', label: 'Drafts' },
                { value: 'scheduled', label: 'Scheduled' },
                { value: 'published', label: 'Published' },
                { value: 'failed', label: 'Failed' },
              ]}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            />
          </div>
          {posts.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={toggleSelectAll}
            >
              {selectedPosts.length === posts.length ? (
                <>
                  <Icon name="checkbox-checked" size="sm" className="mr-2" />
                  Deselect All
                </>
              ) : (
                <>
                  <Icon name="checkbox-empty" size="sm" className="mr-2" />
                  Select All
                </>
              )}
            </Button>
          )}
        </div>
      </Card>

      {/* Posts List */}
      <Card>
        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="h-14 bg-gray-100 rounded mb-2" />
              </div>
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="edit" size="3xl" className="mx-auto mb-4 opacity-40" />
            <p className="text-gray-500 mb-4">
              No {statusFilter !== 'all' ? statusFilter : ''} posts found
            </p>
            <Button variant="outline" onClick={() => navigate('/posts/new')}>
              Create your first post
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {posts.map((post) => (
              <div
                key={post.id}
                className="p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                  {/* Checkbox */}
                  <div className="hidden sm:block pt-1">
                    <button
                      onClick={() => toggleSelectPost(post.id)}
                      className="text-gray-400 hover:text-blue-600"
                    >
                      {selectedPosts.includes(post.id) ? (
                        <Icon name="checkbox-checked" size="md" />
                      ) : (
                        <Icon name="checkbox-empty" size="md" />
                      )}
                    </button>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-medium text-gray-900 truncate">
                        {post.title || 'Untitled Post'}
                      </h3>
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          post.status === 'published'
                            ? 'bg-green-100 text-green-800'
                            : post.status === 'scheduled'
                            ? 'bg-purple-100 text-purple-800'
                            : post.status === 'failed'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {post.status}
                      </span>
                      {post.j2k2_generated && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          J2K2
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                      {post.content}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span className="capitalize">
                        {post.platforms.join(', ')}
                      </span>
                      {post.scheduled_at && (
                        <span>
                          Scheduled: {format(new Date(post.scheduled_at), 'MMM d, yyyy h:mm a')}
                        </span>
                      )}
                      {post.published_at && (
                        <span>
                          Published: {format(new Date(post.published_at), 'MMM d, yyyy')}
                        </span>
                      )}
                      {post.hashtags.length > 0 && (
                        <span>{post.hashtags.length} hashtags</span>
                      )}
                    </div>
                    {post.results && post.results.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {post.results.map((result) => (
                          <span
                            key={result.id}
                            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium capitalize ${
                              result.status === 'success'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                            title={result.error_message || undefined}
                          >
                            {result.platform}
                            {result.post_url && (
                              <a
                                href={result.post_url}
                                target="_blank"
                                rel="noreferrer"
                                className="ml-1 underline"
                                onClick={(e) => e.stopPropagation()}
                              >
                                link
                              </a>
                            )}
                          </span>
                        ))}
                      </div>
                    )}
                    {post.status === 'failed' && post.error_message && (
                      <p className="text-xs text-red-600 mt-2">Error: {post.error_message}</p>
                    )}
                  </div>

                  <div className="flex items-center gap-2 mt-3 sm:mt-0">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/posts/${post.id}`)}
                      className="flex-1 sm:flex-none"
                    >
                      <Icon name="edit" size="sm" className="sm:mr-0 mr-2" />
                      <span className="sm:hidden">Edit</span>
                    </Button>
                    {post.status === 'failed' && (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => handleRetry(post.id)}
                        isLoading={retryingId === post.id}
                        className="flex-1 sm:flex-none"
                      >
                        <Icon name="refresh" size="sm" className="sm:mr-0 mr-2" />
                        <span className="sm:hidden">Retry</span>
                      </Button>
                    )}
                    {deleteConfirm === post.id ? (
                      <>
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => handleDelete(post.id)}
                          className="flex-1 sm:flex-none"
                        >
                          Confirm
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setDeleteConfirm(null)}
                          className="flex-1 sm:flex-none"
                        >
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setDeleteConfirm(post.id)}
                        className="flex-1 sm:flex-none"
                      >
                        <Icon name="trash" size="sm" className="sm:mr-0 mr-2" />
                        <span className="sm:hidden">Delete</span>
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Bulk Schedule Modal */}
      {showBulkSchedule && (
        <BulkScheduleModal
          postIds={selectedPosts}
          onClose={() => setShowBulkSchedule(false)}
          onComplete={handleBulkScheduleComplete}
        />
      )}
    </div>
  );
}
